"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import type { KeyboardEvent, KeyboardEventHandler } from "react";

import Image from "next/image";
import Link from "next/link";
import {
  ArrowRight,
  BookOpen,
  CalendarClock,
  Check,
  CircleDollarSign,
  Clapperboard,
  Instagram,
  Lightbulb,
  MessageCircle,
  Mic2,
  PauseCircle,
  RefreshCw,
  ScanEye,
  Send,
  ShieldCheck,
  Video,
  Workflow,
} from "lucide-react";

import { TiktokIcon } from "@/components/icons/TiktokIcon";
import { YoutubeIcon } from "@/components/icons/YoutubeIcon";
import logo from "@/public/images/logo.png";
import { getLocalizedPath } from "@/utils/utils";

type CategoryId =
  | "plan"
  | "workflows"
  | "production"
  | "review"
  | "automation"
  | "publishing";

type PhaseId = "plan" | "generate" | "review" | "schedule" | "publish";

type CategoryCopy = {
  title: string;
  shortDescription: string;
  description: string;
  coreDescription: string;
  phases: PhaseId[];
  features: [string, string, string, string];
  relatesToSource: boolean;
  relatesToChannels: boolean;
};

type ProductMapCopy = {
  eyebrow: string;
  title: string;
  description: string;
  promiseLead: string;
  promiseBody: string;
  explore: string;
  create: string;
  stats: [string, string, string];
  system: {
    eyebrow: string;
    title: string;
    description: string;
    hint: string;
    toolbarTitle: string;
    currentRelation: string;
    otherFlow: string;
    treeLabel: string;
    treeRoot: string;
    treeRootDescription: string;
    flowLabel: string;
    flowTitle: string;
    currentPrefix: string;
    inputTitle: string;
    inputDescription: string;
    inputs: [string, string, string, string];
    workflowName: string;
    running: string;
    channelTitle: string;
    channelDescription: string;
    footnote: string;
    detailLabel: string;
    detailIncludes: string;
    detailAction: string;
    phaseNames: Record<PhaseId, string>;
    phaseDescriptions: Record<PhaseId, string>;
  };
  categories: Record<CategoryId, CategoryCopy>;
  workflowSection: {
    eyebrow: string;
    title: string;
    description: string;
    items: [
      { title: string; description: string; meta: [string, string] },
      { title: string; description: string; meta: [string, string] },
      { title: string; description: string; meta: [string, string] },
      { title: string; description: string; meta: [string, string] },
    ];
  };
  automationSection: {
    eyebrow: string;
    title: string;
    description: string;
    promise: string;
    items: [
      { title: string; description: string },
      { title: string; description: string },
      { title: string; description: string },
      { title: string; description: string },
    ];
  };
  finalCta: {
    eyebrow: string;
    title: string;
    description: string;
    action: string;
  };
};

const categoryOrder: CategoryId[] = [
  "plan",
  "workflows",
  "production",
  "review",
  "automation",
  "publishing",
];

const phaseOrder: PhaseId[] = ["plan", "generate", "review", "schedule", "publish"];

const categoryVisuals = {
  plan: { Icon: Lightbulb, color: "#0ea5e9", soft: "#eef8ff", textClass: "text-sky-700", bgClass: "bg-sky-50" },
  workflows: { Icon: Workflow, color: "#f59e0b", soft: "#fff8e7", textClass: "text-amber-700", bgClass: "bg-amber-50" },
  production: { Icon: Clapperboard, color: "#f43f5e", soft: "#fff0f3", textClass: "text-rose-700", bgClass: "bg-rose-50" },
  review: { Icon: ScanEye, color: "#10b981", soft: "#ecfdf5", textClass: "text-emerald-700", bgClass: "bg-emerald-50" },
  automation: { Icon: CalendarClock, color: "#8b5cf6", soft: "#f4f0ff", textClass: "text-violet-700", bgClass: "bg-violet-50" },
  publishing: { Icon: Send, color: "#6366f1", soft: "#eef2ff", textClass: "text-indigo-700", bgClass: "bg-indigo-50" },
} as const;

const workflowRoutes = [
  "/dashboard/create-workflow/faceless-video",
  "/dashboard/create-workflow/podcast",
  "/dashboard/create-workflow/english-dialogue",
  "/dashboard/create-workflow/book-review",
];

const workflowIcons = [Video, Mic2, MessageCircle, BookOpen];
const workflowVisuals = [
  "bg-emerald-50 text-emerald-700",
  "bg-rose-50 text-rose-700",
  "bg-sky-50 text-sky-700",
  "bg-amber-50 text-amber-700",
];
const automationIcons = [ShieldCheck, PauseCircle, CircleDollarSign, RefreshCw];

function BranchButton({
  id,
  content,
  active,
  onSelect,
  onKeyDown,
}: {
  id: CategoryId;
  content: CategoryCopy;
  active: boolean;
  onSelect: () => void;
  onKeyDown: KeyboardEventHandler<HTMLButtonElement>;
}) {
  const visual = categoryVisuals[id];
  const Icon = visual.Icon;

  return (
    <button
      id={`capability-${id}`}
      type="button"
      aria-pressed={active}
      aria-controls="capability-detail"
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className="grid w-full grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-[border-color,background-color,color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
      style={{
        borderColor: active ? visual.color : "transparent",
        backgroundColor: active ? visual.soft : "transparent",
      }}
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${visual.bgClass} ${visual.textClass}`}>
        <Icon className="h-[17px] w-[17px]" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-xs font-semibold text-zinc-900">{content.title}</span>
        <span className="mt-0.5 block truncate text-[10px] text-zinc-500">{content.shortDescription}</span>
      </span>
      <span className="text-[10px] font-bold tabular-nums text-zinc-400">
        {String(categoryOrder.indexOf(id) + 1).padStart(2, "0")}
      </span>
    </button>
  );
}

function PlatformChips() {
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      <span className="inline-flex min-h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-600">
        <YoutubeIcon className="h-4 w-4 text-red-500" /> YouTube
      </span>
      <span className="inline-flex min-h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-600">
        <TiktokIcon className="h-4 w-4 text-zinc-950" /> TikTok
      </span>
      <span className="inline-flex min-h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-600">
        <Instagram className="h-4 w-4 text-pink-600" aria-hidden="true" /> Instagram
      </span>
      <span className="inline-flex min-h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-600">
        <span className="flex h-4 w-4 items-center justify-center rounded bg-zinc-100 text-[9px] font-bold text-zinc-950">X</span>
        X
      </span>
    </div>
  );
}

export default function ProductCapabilityMap({ locale = "en" }: { locale?: string }) {
  const t = useTranslations("ProductMap");
  const content = t.raw("copy") as ProductMapCopy;
  const [selectedCategory, setSelectedCategory] = useState<CategoryId>("plan");
  const selected = content.categories[selectedCategory];
  const selectedVisual = categoryVisuals[selectedCategory];
  const createWorkflowHref = getLocalizedPath("/dashboard/create-workflow", locale);

  const selectRelativeCategory = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const direction = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : -1;
    const nextId = categoryOrder[(index + direction + categoryOrder.length) % categoryOrder.length];
    setSelectedCategory(nextId);
    requestAnimationFrame(() => document.getElementById(`capability-${nextId}`)?.focus());
  };

  return (
    <div className="min-w-0 overflow-x-clip bg-white text-zinc-950">
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto grid max-w-[1320px] gap-8 px-4 pb-8 pt-12 sm:px-8 sm:pb-9 sm:pt-16 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-x-12 lg:pt-[72px]">
          <div className="min-w-0">
            <p className="mb-0 inline-flex items-center gap-2.5 text-sm font-semibold text-violet-700">
              <span className="h-px w-6 bg-violet-500" aria-hidden="true" />
              {content.eyebrow}
            </p>
            <h1 className="mb-0 mt-4 max-w-4xl text-[40px] font-semibold leading-[1.06] text-zinc-950 sm:text-5xl lg:text-[64px]">
              {content.title}
            </h1>
            <p className="mb-0 mt-5 max-w-3xl text-base leading-8 text-zinc-600 sm:text-lg">
              {content.description}
            </p>
            <p className="mb-0 mt-6 max-w-3xl border-l-[3px] border-violet-700 py-0.5 pl-4 text-sm font-medium leading-7 text-zinc-700">
              <strong className="font-semibold text-zinc-950">{content.promiseLead}</strong>{" "}
              {content.promiseBody}
            </p>
          </div>

          <div className="grid gap-2.5 sm:grid-cols-2 lg:flex lg:flex-wrap lg:justify-end">
            <a
              href="#system-map"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-violet-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              {content.explore}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <Link
              href={createWorkflowHref}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-800 transition-colors hover:border-zinc-500 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              {content.create}
            </Link>
          </div>

          <div className="grid grid-cols-3 border-t border-zinc-200 pt-5 lg:col-span-2">
            {content.stats.map((stat, index) => (
              <div
                key={stat}
                className="flex min-w-0 items-start gap-2 border-l border-zinc-200 px-2 first:border-l-0 first:pl-0 sm:items-center sm:gap-3 sm:px-6"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-[9px] font-bold tabular-nums text-emerald-700 sm:h-7 sm:w-7 sm:text-[10px]">
                  0{index === 0 ? 6 : index === 1 ? 5 : 4}
                </span>
                <span className="min-w-0 text-[11px] font-medium leading-5 text-zinc-600 sm:text-sm">
                  {stat}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="system-map" className="scroll-mt-20 border-b border-zinc-200 bg-[#f6f6f8]">
        <div className="mx-auto max-w-[1320px] px-4 py-14 sm:px-8 sm:py-20">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="mb-0 text-sm font-semibold text-violet-700">{content.system.eyebrow}</p>
              <h2 className="mb-0 mt-3 text-3xl font-semibold leading-tight text-zinc-950 sm:text-4xl">
                {content.system.title}
              </h2>
              <p className="mb-0 mt-4 text-base leading-7 text-zinc-600">{content.system.description}</p>
            </div>
            <p className="mb-0 inline-flex items-center gap-2 text-sm font-medium text-zinc-500">
              <span className="h-2 w-2 rounded-full bg-violet-600 shadow-[0_0_0_5px_rgba(109,40,217,0.1)]" />
              {content.system.hint}
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-lg border border-zinc-300 bg-white shadow-[0_22px_70px_rgba(24,24,27,0.12)]">
            <div className="flex min-h-14 flex-col items-start justify-between gap-2 border-b border-zinc-200 px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
              <p className="mb-0 flex min-w-0 items-center gap-2.5 text-xs font-semibold text-zinc-800">
                <span className="relative h-2 w-2 shrink-0 rounded-full bg-emerald-500 after:absolute after:-inset-1 after:rounded-full after:border after:border-emerald-500/30" />
                {content.system.toolbarTitle}
              </p>
              <div className="flex items-center gap-4 text-[11px] font-medium text-zinc-500">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 rounded-[3px] border"
                    style={{ borderColor: selectedVisual.color, backgroundColor: selectedVisual.soft }}
                  />
                  {content.system.currentRelation}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-[3px] border border-zinc-300 bg-white" />
                  {content.system.otherFlow}
                </span>
              </div>
            </div>

            <div className="grid min-h-[650px] xl:grid-cols-[252px_minmax(500px,1fr)_330px]">
              <aside className="order-1 border-b border-zinc-200 bg-[#fbfbfc] p-4 xl:order-none xl:border-b-0 xl:border-r">
                <p className="mb-0 text-[10px] font-bold uppercase text-zinc-500">{content.system.treeLabel}</p>
                <div className="mt-3 flex items-center gap-3 rounded-lg border border-zinc-900 bg-zinc-950 p-3 text-white">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white">
                    <Image src={logo} alt="" width={28} height={28} className="rounded-md" />
                  </span>
                  <span className="min-w-0">
                    <strong className="block text-sm font-semibold">{content.system.treeRoot}</strong>
                    <span className="mt-0.5 block text-[10px] text-zinc-400">{content.system.treeRootDescription}</span>
                  </span>
                </div>

                <div className="relative mt-4">
                  <span className="absolute bottom-0 left-[5px] top-0 hidden w-px bg-zinc-300 xl:block" aria-hidden="true" />
                  <ol className="scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 xl:mx-0 xl:grid xl:gap-2 xl:overflow-visible xl:px-0 xl:pl-[18px]">
                    {categoryOrder.map((id, index) => (
                      <li key={id} className="relative min-w-[190px] xl:min-w-0 xl:before:absolute xl:before:-left-[13px] xl:before:top-1/2 xl:before:w-[13px] xl:before:border-t xl:before:border-zinc-300">
                        <BranchButton
                          id={id}
                          content={content.categories[id]}
                          active={selectedCategory === id}
                          onSelect={() => setSelectedCategory(id)}
                          onKeyDown={(event) => selectRelativeCategory(event, index)}
                        />
                      </li>
                    ))}
                  </ol>
                </div>
              </aside>

              <div className="order-3 min-w-0 p-4 sm:p-6 xl:order-none xl:border-r xl:border-zinc-200">
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
                  <div>
                    <p className="mb-0 text-[10px] font-bold uppercase text-zinc-500">{content.system.flowLabel}</p>
                    <h3 className="mb-0 mt-1 text-base font-semibold text-zinc-950">{content.system.flowTitle}</h3>
                  </div>
                  <span
                    className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[10px] font-semibold text-zinc-800"
                    style={{ borderColor: selectedVisual.color, backgroundColor: selectedVisual.soft }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: selectedVisual.color }} />
                    {content.system.currentPrefix}: {selected.title}
                  </span>
                </div>

                <div className="mt-6 grid justify-items-center">
                  <div
                    className="w-full rounded-lg border bg-zinc-50 p-3 transition-colors"
                    style={selected.relatesToSource ? { borderColor: selectedVisual.color, backgroundColor: selectedVisual.soft } : undefined}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <strong className="text-[11px] font-semibold text-zinc-900">{content.system.inputTitle}</strong>
                      <span className="text-[9px] font-medium text-zinc-500">{content.system.inputDescription}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {content.system.inputs.map((input) => (
                        <span key={input} className="inline-flex min-h-7 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[10px] font-semibold text-zinc-600">
                          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />
                          {input}
                        </span>
                      ))}
                    </div>
                  </div>

                  <span className="relative h-6 w-px bg-zinc-300 after:absolute after:-bottom-px after:-left-[3px] after:h-1.5 after:w-1.5 after:rotate-45 after:border-b after:border-r after:border-zinc-400" aria-hidden="true" />

                  <div className="flex w-full max-w-[390px] items-center gap-3 rounded-lg border border-zinc-950 bg-zinc-950 p-3 text-white shadow-[0_14px_36px_rgba(24,24,27,0.18)]">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white">
                      <Image src={logo} alt="" width={34} height={34} className="rounded-md" />
                    </span>
                    <span className="min-w-0">
                      <strong className="block text-sm font-semibold">{content.system.workflowName}</strong>
                      <span className="mt-1 block truncate text-[10px] font-medium text-violet-300">{selected.coreDescription}</span>
                    </span>
                    <span className="ml-auto shrink-0 rounded-md border border-white/15 px-2 py-1 text-[9px] font-semibold text-zinc-300">
                      {content.system.running}
                    </span>
                  </div>

                  <span className="relative h-6 w-px bg-zinc-300 after:absolute after:-bottom-px after:-left-[3px] after:h-1.5 after:w-1.5 after:rotate-45 after:border-b after:border-r after:border-zinc-400" aria-hidden="true" />

                  <div className="scrollbar-hide w-full overflow-x-auto pb-1">
                    <ol className="grid min-w-[500px] grid-cols-5">
                      {phaseOrder.map((phase, index) => {
                        const active = selected.phases.includes(phase);
                        return (
                          <li key={phase} className="relative min-w-0 px-2 text-center after:absolute after:left-[calc(50%+20px)] after:top-[17px] after:h-px after:w-[calc(100%-40px)] after:bg-zinc-300 last:after:hidden">
                            <span
                              className="relative z-10 mx-auto flex h-9 w-9 items-center justify-center rounded-lg border bg-white text-[11px] font-bold tabular-nums text-zinc-500 transition-colors"
                              style={active ? { borderColor: selectedVisual.color, backgroundColor: selectedVisual.soft, color: selectedVisual.color } : undefined}
                            >
                              0{index + 1}
                            </span>
                            <strong className="mt-2 block text-[10px] font-semibold text-zinc-700">{content.system.phaseNames[phase]}</strong>
                            <span className="mt-1 block text-[9px] leading-4 text-zinc-400">{content.system.phaseDescriptions[phase]}</span>
                          </li>
                        );
                      })}
                    </ol>
                  </div>

                  <span className="relative h-6 w-px bg-zinc-300 after:absolute after:-bottom-px after:-left-[3px] after:h-1.5 after:w-1.5 after:rotate-45 after:border-b after:border-r after:border-zinc-400" aria-hidden="true" />

                  <div
                    className="w-full rounded-lg border bg-zinc-50 p-3 transition-colors"
                    style={selected.relatesToChannels ? { borderColor: selectedVisual.color, backgroundColor: selectedVisual.soft } : undefined}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <strong className="text-[11px] font-semibold text-zinc-900">{content.system.channelTitle}</strong>
                      <span className="text-[9px] font-medium text-zinc-500">{content.system.channelDescription}</span>
                    </div>
                    <PlatformChips />
                  </div>
                </div>

                <p className="mb-0 mt-4 flex items-center justify-center gap-2 text-center text-[10px] font-medium leading-4 text-zinc-500">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {content.system.footnote}
                </p>
              </div>

              <aside
                id="capability-detail"
                aria-live="polite"
                className="order-2 flex min-h-[430px] min-w-0 flex-col border-b border-zinc-200 bg-white p-5 xl:order-none xl:min-h-0 xl:border-b-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-[10px] font-semibold text-zinc-500">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: selectedVisual.color }} />
                    {content.system.detailLabel}
                  </span>
                  <span className="text-[10px] font-bold tabular-nums text-zinc-400">
                    {String(categoryOrder.indexOf(selectedCategory) + 1).padStart(2, "0")} / 06
                  </span>
                </div>

                <h3 className="mb-0 mt-4 text-2xl font-semibold leading-tight text-zinc-950">{selected.title}</h3>
                <p className="mb-0 mt-3 text-sm leading-6 text-zinc-600">{selected.description}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {selected.phases.map((phase) => (
                    <span
                      key={phase}
                      className="inline-flex min-h-7 items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold text-zinc-700"
                      style={{ borderColor: selectedVisual.color, backgroundColor: selectedVisual.soft }}
                    >
                      {content.system.phaseNames[phase]}
                    </span>
                  ))}
                </div>

                <div className="my-5 h-px bg-zinc-200" />
                <p className="mb-0 text-[10px] font-bold uppercase text-zinc-500">{content.system.detailIncludes}</p>
                <div className="mt-3 grid gap-2.5">
                  {selected.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2.5 text-xs font-medium leading-5 text-zinc-700">
                      <span
                        className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: selectedVisual.soft, color: selectedVisual.color }}
                      >
                        <Check className="h-3 w-3" aria-hidden="true" />
                      </span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  href={createWorkflowHref}
                  className="mt-auto flex min-h-11 items-center justify-between gap-3 border-t border-zinc-200 pt-5 text-xs font-semibold text-violet-700 hover:text-violet-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                >
                  {content.system.detailAction}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </aside>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-[1320px] px-4 py-14 sm:px-8 sm:py-20">
          <div className="max-w-3xl">
            <p className="mb-0 text-sm font-semibold text-violet-700">{content.workflowSection.eyebrow}</p>
            <h2 className="mb-0 mt-3 text-3xl font-semibold leading-tight text-zinc-950 sm:text-4xl">{content.workflowSection.title}</h2>
            <p className="mb-0 mt-4 text-base leading-7 text-zinc-600">{content.workflowSection.description}</p>
          </div>

          <div className="mt-9 grid border-l border-t border-zinc-200 md:grid-cols-2">
            {content.workflowSection.items.map((item, index) => {
              const Icon = workflowIcons[index];
              return (
                <article key={item.title} className="grid min-h-[178px] grid-cols-[44px_minmax(0,1fr)_36px] items-start gap-4 border-b border-r border-zinc-200 p-5 transition-colors hover:bg-zinc-50 sm:grid-cols-[48px_minmax(0,1fr)_36px] sm:p-6">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-lg sm:h-12 sm:w-12 ${workflowVisuals[index]}`}>
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="mb-0 text-base font-semibold text-zinc-950">{item.title}</h3>
                    <p className="mb-0 mt-2 text-sm leading-6 text-zinc-600">{item.description}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {item.meta.map((meta) => (
                        <span key={meta} className="rounded-full border border-zinc-200 px-2 py-1 text-[10px] font-medium text-zinc-500">{meta}</span>
                      ))}
                    </div>
                  </div>
                  <Link
                    href={getLocalizedPath(workflowRoutes[index], locale)}
                    aria-label={`${content.create}: ${item.title}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-300 text-zinc-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
                  >
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-zinc-950 text-white">
        <div className="mx-auto grid max-w-[1320px] gap-10 px-4 py-14 sm:px-8 sm:py-20 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <div>
            <p className="mb-0 text-sm font-semibold text-violet-300">{content.automationSection.eyebrow}</p>
            <h2 className="mb-0 mt-3 text-3xl font-semibold leading-tight text-white sm:text-4xl">{content.automationSection.title}</h2>
            <p className="mb-0 mt-4 text-base leading-7 text-zinc-400">{content.automationSection.description}</p>
            <p className="mb-0 mt-7 border-t border-zinc-700 pt-6 text-base font-medium leading-8 text-zinc-200">{content.automationSection.promise}</p>
          </div>

          <div className="grid border-l border-t border-zinc-700 sm:grid-cols-2">
            {content.automationSection.items.map((item, index) => {
              const Icon = automationIcons[index];
              return (
                <article key={item.title} className="min-h-[170px] border-b border-r border-zinc-700 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-800 text-violet-300">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="mb-0 mt-4 text-sm font-semibold text-white">{item.title}</h3>
                  <p className="mb-0 mt-2 text-xs leading-5 text-zinc-400">{item.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-violet-700 text-white">
        <div className="mx-auto flex max-w-[1320px] flex-col items-start justify-between gap-8 px-4 py-12 sm:px-8 sm:py-16 lg:flex-row lg:items-center lg:gap-12">
          <div>
            <p className="mb-0 text-sm font-semibold text-violet-200">{content.finalCta.eyebrow}</p>
            <h2 className="mb-0 mt-3 max-w-3xl text-3xl font-semibold leading-tight text-white sm:text-4xl">{content.finalCta.title}</h2>
            <p className="mb-0 mt-4 max-w-2xl text-base leading-7 text-violet-100">{content.finalCta.description}</p>
          </div>
          <Link
            href={createWorkflowHref}
            className="inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-violet-800 transition-colors hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-violet-700 sm:w-auto"
          >
            {content.finalCta.action}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
